import React from "react";
import { motion } from "framer-motion";
import { ExternalLink } from "lucide-react";

const AIRBNB_PATH =
  "M12.001 18.275c-1.353-1.697-2.148-3.184-2.413-4.457-.263-1.027-.16-1.848.291-2.465.477-.71 1.188-1.056 2.121-1.056s1.643.345 2.12 1.063c.446.61.558 1.432.286 2.465-.291 1.298-1.085 2.785-2.412 4.458zm9.601 1.14c-.185 1.246-1.034 2.28-2.2 2.783-2.253.98-4.483-.583-6.392-2.704 3.157-3.951 3.74-7.028 2.385-9.018-.795-1.14-1.933-1.695-3.394-1.695-2.944 0-4.563 2.49-3.927 5.382.37 1.565 1.352 3.343 2.917 5.332-.98 1.085-1.91 1.856-2.732 2.333-.636.344-1.245.558-1.828.609-2.679.399-4.778-2.2-3.825-4.88.132-.345.395-.98.845-1.961l.025-.053c1.464-3.178 3.242-6.79 5.285-10.795l.053-.132.58-1.116c.45-.822.635-1.19 1.351-1.643.346-.21.77-.315 1.246-.315.954 0 1.698.558 2.016 1.007.158.239.345.557.582.953l.558 1.089.08.159c2.041 4.004 3.821 7.608 5.279 10.794l.026.025.533 1.22.318.764c.243.613.294 1.222.213 1.858zm1.22-2.39c-.186-.583-.505-1.271-.9-2.094v-.03c-1.889-4.006-3.642-7.608-5.307-10.844l-.111-.163C15.317 1.461 14.468 0 12.001 0c-2.44 0-3.476 1.695-4.535 3.898l-.081.16c-1.669 3.236-3.421 6.843-5.303 10.847v.053l-.559 1.22c-.21.504-.317.768-.345.847C-.172 20.74 2.611 24 5.98 24c.027 0 .132 0 .265-.027h.372c1.75-.213 3.554-1.325 5.384-3.317 1.829 1.989 3.635 3.104 5.382 3.317h.372c.133.027.239.027.265.027 3.37.003 6.152-3.261 4.802-6.975z";

const BookSection = ({ property }) => {
  const airbnbUrl = property?.airbnb_url;
  const displayUrl = airbnbUrl ? airbnbUrl.replace(/^https?:\/\/(www\.)?/, "") : "";
  return (
    <section id="enquire" className="border-t border-[#D6B15E]/20 bg-[#04271B]" data-testid="book-section">
      <div className="max-w-4xl mx-auto px-6 sm:px-10 py-24 text-center flex flex-col items-center">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-xs tracking-[0.25em] uppercase text-[#D6B15E] mb-4"
        >
          Book your stay
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="font-heading font-light text-4xl sm:text-5xl tracking-tight leading-tight max-w-2xl"
        >
          Ready to experience {property?.name || "the hut"} for real?
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="text-[#A9B8A6] font-light leading-relaxed text-lg mt-6 max-w-lg"
        >
          Check availability, see guest reviews and book securely on Airbnb.
        </motion.p>

        <motion.a
          href={airbnbUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="airbnb-book-btn"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="group mt-10 inline-flex items-center gap-3 bg-[#FF385C] text-white px-8 py-4 rounded-full text-sm tracking-widest uppercase hover:bg-[#e11d48] transition-colors duration-300 shadow-[0_8px_30px_rgba(255,56,92,0.35)]"
        >
          <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current" aria-hidden="true">
            <path d={AIRBNB_PATH} />
          </svg>
          View on Airbnb
          <ExternalLink className="w-4 h-4 opacity-70 group-hover:translate-x-0.5 transition-transform" strokeWidth={1.5} />
        </motion.a>

        <p className="mt-6 text-sm text-[#6f8378] tracking-wide">{displayUrl}</p>
      </div>

      <div className="border-t border-[#D6B15E]/20">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/logo-hut.png" alt="Cheshire Holidays" className="h-9 w-auto object-contain" />
            <span className="font-heading text-[#D6B15E] text-lg">Cheshire Holidays</span>
          </div>
          <p className="text-xs tracking-widest uppercase text-[#6f8378]">Immersive 360° Property Tours</p>
        </div>
      </div>
    </section>
  );
};

export default BookSection;
